// Core State Management - Authentication Store
// Location: src/core/store/authStore.ts

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { authService } from "features/auth/services/authService";
import type {
  AuthState,
  User,
  RegisterData,
  UserRole,
} from "../../types/auth.types";
import type { AuthUserPayload } from "features/auth/services/authService";
import { useProviderStore } from "./providerStore";

let inMemoryToken: string | null = null;
export const getAccessToken = (): string | null => inMemoryToken;
export const setAccessToken = (token: string | null): void => {
  inMemoryToken = token;
};

const mapAppRoleToUserRole = (
  appRole?: string,
  platformRole?: string | null,
): UserRole => {
  if (platformRole) return "admin";
  if (appRole === "provider") return "provider";
  return "customer";
};

const userFromAuthPayload = (userData: AuthUserPayload): User => ({
  uid: userData.userId,
  email: userData.email,
  displayName:
    [userData.firstName, userData.lastName].filter(Boolean).join(" ") || null,
  role: mapAppRoleToUserRole(userData.appRole, userData.platformRole),
});

const syncProviderState = (
  me: Awaited<ReturnType<typeof authService.getMe>>,
): void => {
  const store = useProviderStore.getState();
  store.setOrgMemberships(me.orgMemberships ?? []);
  if (
    me.appRole === "provider" &&
    !me.platformRole &&
    store.vehicles.length === 0 &&
    !store.vehiclesLoading
  ) {
    void store.refreshVehicles();
  }
};

const userFromMe = (
  me: Awaited<ReturnType<typeof authService.getMe>>,
): User => {
  syncProviderState(me);
  return {
    uid: me.id,
    email: me.email,
    displayName: [me.firstName, me.lastName].filter(Boolean).join(" ") || null,
    role: mapAppRoleToUserRole(me.appRole, me.platformRole),
    orgMemberships: me.orgMemberships,
    platformPermissions: me.platformPermissions,
  };
};

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      activeView: null,

      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user,
          error: null,
        });
      },

      setLoading: (isLoading: boolean) => set({ isLoading }),

      setError: (error: string | null) => set({ error }),

      setActiveView: (view) => set({ activeView: view }),

      hydrateFromMe: async (): Promise<User | null> => {
        const token = getAccessToken();
        if (!token) return null;
        try {
          const me = await authService.getMe(token);
          const user = userFromMe(me);
          set({ user, isAuthenticated: true, error: null });
          return user;
        } catch {
          return null;
        }
      },

      login: async (email: string, password: string): Promise<User> => {
        set({ isLoading: true, error: null });
        try {
          const { user: userData, accessToken } = await authService.login(
            email,
            password,
          );
          setAccessToken(accessToken);
          const user = userFromAuthPayload(userData);
          set({ user, isAuthenticated: true, isLoading: false, error: null });
          await get().hydrateFromMe();
          return get().user ?? user;
        } catch (error: any) {
          const message =
            error?.response?.data?.message || "Login failed. Please try again.";
          set({ error: message, isLoading: false, isAuthenticated: false });
          throw new Error(message);
        }
      },

      register: async (data: RegisterData): Promise<User> => {
        set({ isLoading: true, error: null });
        try {
          const { user: userData, accessToken } =
            await authService.register(data);
          setAccessToken(accessToken);
          const user = userFromAuthPayload(userData);
          set({ user, isAuthenticated: true, isLoading: false, error: null });
          await get().hydrateFromMe();
          return get().user ?? user;
        } catch (error: any) {
          const message =
            error?.response?.data?.message ||
            "Registration failed. Please try again.";
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      logout: async (): Promise<void> => {
        set({ isLoading: true });
        try {
          await authService.logout(getAccessToken());
        } catch {
          // ignore
        } finally {
          setAccessToken(null);
          useProviderStore.getState().reset();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            activeView: null,
          });
        }
      },

      initAuthListener: () => {
        let stale = false;
        set({ isLoading: true });
        authService
          .refreshToken()
          .then(({ accessToken }) => {
            if (stale) return;
            if (!accessToken) throw new Error("No token in refresh response");
            setAccessToken(accessToken);
            return authService.getMe(accessToken);
          })
          .then((userData) => {
            if (stale || !userData) return;
            const user = userFromMe(userData);
            set({ user, isAuthenticated: true, isLoading: false });
          })
          .catch(() => {
            if (stale) return;
            setAccessToken(null);
            useProviderStore.getState().reset();
            set({ user: null, isAuthenticated: false, isLoading: false });
          });
        return () => {
          stale = true;
        };
      },

      hasRole: (role) => {
        const { user } = get();
        return user?.role === role;
      },

      clearError: () => set({ error: null }),
    }),
    { name: "AuthStore", enabled: import.meta.env.DEV },
  ),
);
